// Razorpay Checkout Standard loader + modal helper.
//
// We use Razorpay's hosted checkout JS rather than the hosted Subscription
// URL because subscriptions don't accept callback_url (BAD_REQUEST_ERROR
// "callback_url is/are not required and should not be sent"). The Checkout
// modal renders in-page on chitos.chitdesk.com, so the operator never
// leaves our domain and we control the post-payment transition via the
// `handler` callback.
//
// The key_id is intentionally public (Razorpay's design — only key_secret
// stays server-side). Exposed to the bundle via VITE_RAZORPAY_KEY_ID.

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => { open(): void; close(): void };
  }
}

interface RazorpayOptions {
  key: string;
  subscription_id?: string;
  order_id?: string;
  amount?: number;
  currency?: string;
  name: string;
  description?: string;
  image?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  theme?: { color?: string };
  handler: (response: RazorpaySuccessResponse) => void;
  modal?: {
    ondismiss?: () => void;
    confirm_close?: boolean;
    escape?: boolean;
  };
}

export interface RazorpaySuccessResponse {
  razorpay_payment_id: string;
  razorpay_subscription_id?: string;
  razorpay_order_id?: string;
  razorpay_signature: string;
}

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let load_promise: Promise<void> | null = null;

/** Load Razorpay's checkout.js once + cache the promise so callers de-dup. */
export function load_razorpay(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR not supported'));
  if (window.Razorpay) return Promise.resolve();
  if (load_promise) return load_promise;
  load_promise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Razorpay checkout.js failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      load_promise = null;
      reject(new Error('Razorpay checkout.js failed to load (network or CSP)'));
    };
    document.head.appendChild(script);
  });
  return load_promise;
}

export interface OpenSubscriptionCheckoutArgs {
  subscription_id: string;
  /** Merchant display name shown in modal header. */
  name?: string;
  /** Short subtitle — e.g. "Starter (monthly)". */
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  on_success: (response: RazorpaySuccessResponse) => void;
  on_dismiss?: () => void;
}

/** Open Razorpay Checkout for a subscription mandate authentication. */
export async function open_subscription_checkout(
  args: OpenSubscriptionCheckoutArgs,
): Promise<void> {
  await load_razorpay();
  const key = import.meta.env.VITE_RAZORPAY_KEY_ID;
  if (!key) {
    throw new Error('VITE_RAZORPAY_KEY_ID is not configured in the studio build');
  }
  if (!window.Razorpay) throw new Error('Razorpay checkout.js loaded but window.Razorpay undefined');
  const rzp = new window.Razorpay({
    key,
    subscription_id: args.subscription_id,
    name: args.name ?? 'ChitDesk',
    description: args.description,
    prefill: args.prefill,
    theme: { color: '#7c1f37' },
    handler: args.on_success,
    modal: {
      confirm_close: true,
      ondismiss: args.on_dismiss,
    },
  });
  rzp.open();
}
